import React, { useEffect, useRef, useState } from "react";
import * as api from "./csaApi";
import "./CsaNotePad.css";

/**
 * The note pad — a panel inside the audit, not a screen (design §7e).
 *
 * Deliberately NOT the existing Notes feature. Notes there never reach a
 * report, so everything written in them gets retyped; that is the double entry
 * this replaces. `NewNote`, `NotesListScreen`, `ViewNote` and `notesModel` are
 * untouched by CSA.
 *
 * Walking the plant, an auditor types one line and moves on — no question
 * chosen, five seconds. At the desk, the write-up screen offers those notes
 * back under each gap.
 */
export default function CsaNotePad({ auditId, open, onClose, subPillars, currentSubPillar, onCountChange }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Default the tag to wherever the auditor is standing in the checklist.
    // Most will not bother tagging, and that is fine (§7e step 2) — but when
    // they are already inside a sub-pillar, guessing it costs nothing.
    setTag(currentSubPillar || "");
    let live = true;
    api
      .listNotes(auditId)
      .then((rows) => live && setNotes(Array.isArray(rows) ? rows : []))
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [open, auditId, currentSubPillar]);

  useEffect(() => {
    if (open && box.current) box.current.focus();
  }, [open]);

  useEffect(() => {
    if (onCountChange) onCountChange(notes.filter((n) => !n.used_for).length);
  }, [notes, onCountChange]);

  const add = async () => {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    try {
      const row = await api.addNote(auditId, text, tag || null);
      // Prepend rather than re-fetch: the list is already correct, and once the
      // outbox exists a re-fetch would not show the note that has not synced.
      setNotes((prev) => [row, ...prev.filter((n) => n.id !== row.id)]);
      setBody("");
      if (box.current) box.current.focus();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteNote(auditId, id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  const onKey = (e) => {
    // Enter adds; Shift+Enter is a new line. A note is one line by design.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      add();
    }
  };

  if (!open) return null;

  const unused = notes.filter((n) => !n.used_for);
  const used = notes.filter((n) => n.used_for);

  return (
    <>
      <div className="csa-pad-scrim" onClick={onClose} />
      <aside className="csa-pad" aria-label="Note pad">
        <header>
          <div>
            <h2>Note pad</h2>
            <p>
              {unused.length} unused
              {used.length ? ` · ${used.length} written up` : ""}
            </p>
          </div>
          <button type="button" className="pad-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="pad-new">
          <textarea
            ref={box}
            rows={2}
            value={body}
            placeholder="One line — what you just saw."
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={onKey}
          />
          <div className="pad-new-row">
            <select value={tag} onChange={(e) => setTag(e.target.value)}>
              <option value="">No tag</option>
              {(subPillars || []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button type="button" onClick={add} disabled={!body.trim() || busy}>
              Add
            </button>
          </div>
        </div>

        {error ? <div className="pad-error">{error}</div> : null}

        <div className="pad-list">
          {unused.length === 0 && used.length === 0 ? (
            <p className="pad-empty">
              Nothing yet. Jot what you see as you walk — you write it up at the desk.
            </p>
          ) : null}

          {unused.map((n) => (
            <div className="pad-note" key={n.id}>
              <p>{n.body}</p>
              <div className="pad-meta">
                {n.sub_pillar ? <span className="pad-tag">{n.sub_pillar}</span> : null}
                <span>{when(n.created_at)}</span>
                <button type="button" onClick={() => remove(n.id)}>
                  Discard
                </button>
              </div>
            </div>
          ))}

          {used.length ? <div className="pad-divider">Written up</div> : null}

          {used.map((n) => (
            <div className="pad-note done" key={n.id}>
              <p>{n.body}</p>
              <div className="pad-meta">
                <span className="pad-tag">→ {n.used_for}</span>
                <span>{when(n.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}

function when(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
